import { observer } from "mobx-react";
import { customizedFreetextTypeTitle, FreetextForm, FreetextTypeSelection } from "./freetextDomain";
import { YesNoQuestion } from "@/dbot/common/commonComponents";
import { FC, useEffect } from "react";
import { Box, Grid, VBox } from "@/components-v2/Box";
import { Select } from "@/components-v2/Select";
import { DocumentItem } from "@/dbot/common/commonDomain";
import { useTranslation } from "@/stores/RootStoreContext";
import { RadioFieldQuestion } from "@/common/commonComponents";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";

const Descriptions: FC<{ form: FreetextForm; documentId?: string }> = observer(
  ({ form, documentId }) => {
    return (
      <VBox>
        {form.descriptionTexts
          .filter((d) => !documentId || documentId === d.document.documentId)
          .map(({ document, textModel }) => (
            <LocalizedTextEditor
              key={`${form.decisionId}_${document.documentId}`}
              model={textModel}
            />
          ))}
      </VBox>
    );
  },
);

const FreetextTypeView: FC<{ form: FreetextForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  if (form.freetextTypeModel === undefined) return null;
  return (
    <VBox>
      {form.isCustomized ? (
        form.documents.length > 0 && (
          <>
            <Box>{t("decisions.freetext.freetextType.handling")}</Box>
            <Grid inline cols={2} alignItems={"center"}>
              {form.documents.map((d) => (
                <VBox key={d.documentId} grow>
                  {t(customizedFreetextTypeTitle(d.type))}:
                  <Select<FreetextTypeSelection>
                    items={form.freetextTypeOptions}
                    onSelect={(selected) =>
                      form.selectCustomizedFreetextTypeForDocument(selected.type, d.type)
                    }
                    idFn={(item) => item.type}
                    labelFn={(item) => t(item.label)}
                    iconFn={(option) => option.icon}
                    value={form.getSelectedFreetextTypeForDocument(d.type)}
                    disabled={form.isProjectLocked}
                  />
                </VBox>
              ))}
            </Grid>
          </>
        )
      ) : (
        <RadioFieldQuestion
          question={{ key: "decisions.freetext.freetextType" }}
          fieldName={"freetextType"}
          field={form.freetextTypeModel}
          isLocked={form.isProjectLocked}
        />
      )}
    </VBox>
  );
});

export const FreetextView = observer(function FreetextView({
  form,
  documentContext,
}: {
  form: FreetextForm;
  documentContext: DocumentItem | undefined;
}) {
  const { t } = useTranslation();

  useEffect(() => form.init(), [form]);

  return (
    <VBox gap={"lg"}>
      <LocalizedTextEditor model={form.localizedTitles} inputType="string" autofocus />
      {form.showFreetextType && <FreetextTypeView form={form} />}
      {!form.isCustomized && form.showRequiresGeneralMeeting(documentContext) && (
        <YesNoQuestion
          question={t("decisions.freetext.requiresGM")}
          field={form.requiresGeneralMeeting}
          onChange={(e) => form.setRequiresGeneralMeeting(e)}
          isLocked={form.isProjectLocked || form.isGeneralMeetingMinutesLocked}
        />
      )}
      <Descriptions form={form} documentId={documentContext?.documentId} />
    </VBox>
  );
});
